import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { append, patch, removeItem } from '@ngxs/store/operators';
import { ApplicationFilesService } from '@tran-monorepo/frontend/services';
import { Application } from '@tran-monorepo/interfaces/applications';
import { File as FileData } from '@tran-monorepo/interfaces/files';
import { Page, PageMetadata, SortOrder } from '@tran-monorepo/interfaces/pagination';
import { finalize, Observable, tap } from 'rxjs';
import { ApplicationsService } from '../../../../shared/services/applications.service';
import { CustomerApplicationsOverviewStateModel } from './applications-overview-state.model';
import {
    appOverviewLoadDefaultPageSize,
    DeleteApplication,
    LoadApplications,
    LoadMoreApplications,
    ResetApplicationOverviewState
} from './applications-overview.action';

const DEFAULT_STATE: CustomerApplicationsOverviewStateModel = {
    applications: [],
    metadata: null,
    pageSize: appOverviewLoadDefaultPageSize,
    isLoadingApplications: false,
    filesByApplicationId: {}
};

@State<CustomerApplicationsOverviewStateModel>({
    name: 'customerApplicationsOverviewState',
    defaults: DEFAULT_STATE
})
@Injectable()
export class CustomerApplicationsOverviewState {
    private readonly applicationService = inject(ApplicationsService);
    private readonly filesService = inject(ApplicationFilesService);

    @Selector()
    public static applications(state: CustomerApplicationsOverviewStateModel): Application[] {
        return state.applications;
    }

    @Selector()
    public static isLoadingApplications(state: CustomerApplicationsOverviewStateModel): boolean {
        return state?.isLoadingApplications;
    }

    @Selector()
    public static hasMorePages(state: CustomerApplicationsOverviewStateModel): boolean {
        return state.metadata?.hasNextPage ?? false;
    }

    @Selector()
    public static filesByApplicationId(state: CustomerApplicationsOverviewStateModel): Record<string, FileData[]> {
        return state.filesByApplicationId;
    }

    @Action(LoadApplications)
    public loadApplications(
        { patchState }: StateContext<CustomerApplicationsOverviewStateModel>,
        { payload: { pageSize } }: LoadApplications
    ): Observable<Page<Application, PageMetadata>> {
        patchState({ isLoadingApplications: true });
        return this.applicationService.getUserApplications({ page: 0, pageSize, sortOrder: SortOrder.ASC }).pipe(
            tap(({ items: applications, metadata }) => patchState({ applications, metadata })),
            finalize(() => patchState({ isLoadingApplications: false }))
        );
    }

    @Action(LoadMoreApplications)
    public loadMoreApplications(
        ctx: StateContext<CustomerApplicationsOverviewStateModel>
    ): Observable<Page<Application, PageMetadata>> {
        const { metadata, pageSize } = ctx.getState();
        ctx.patchState({ isLoadingApplications: true });

        const previousPage = metadata?.currentPage ?? 0;
        const page = previousPage + 1;

        return this.applicationService.getUserApplications({ page, pageSize, sortOrder: SortOrder.ASC }).pipe(
            tap(({ items: newApplications, metadata }) =>
                ctx.setState(
                    patch<CustomerApplicationsOverviewStateModel>({
                        applications: append<Application>(newApplications),
                        metadata
                    })
                )
            ),
            finalize(() => ctx.patchState({ isLoadingApplications: false }))
        );
    }

    @Action(DeleteApplication)
    public deleteApplication(
        ctx: StateContext<CustomerApplicationsOverviewStateModel>,
        { applicationId }: DeleteApplication
    ): Observable<void> {
        return this.applicationService.deleteApplication(applicationId).pipe(
            tap(() => {
                ctx.setState(
                    patch<CustomerApplicationsOverviewStateModel>({
                        applications: removeItem<Application>(app => app.id === applicationId)
                    })
                );
            })
        );
    }

    @Action(ResetApplicationOverviewState)
    public resetState({ setState }: StateContext<CustomerApplicationsOverviewStateModel>): void {
        setState(DEFAULT_STATE);
    }
}
